import React, {useEffect, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Accordion, Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {DateInput} from '@mantine/dates';
import {useParams} from 'react-router-dom';
import type {
  CourseDefinitionContentResponse,
  CourseSessionContentResponse,
} from 'src/api/ProactApiClient';
import {
  CourseDefinitionContentTypeEnum,
  CourseSessionContentStateEnum,
  CourseSessionStateEnum,
  CourseSessionTypeEnum,
  EntityIdentifierEnum,
  ModuleEnum,
  SkillSignOffCriterionResultEnum,
  SkillSignOffResultEnum,
} from 'src/api/ProactApiClient';
import {
  useGetCourseSessionQuery,
  useUpdateCourseSessionMutation,
} from 'src/redux/queries/courseSession';
import {Button} from 'src/design/atoms/Button';
import {Subtitle} from 'src/design/atoms/Typography';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {useUrlQueryTabV2} from '../../../../../shared/hooks/useUrlQueryTabV2.tsx';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {
  SessionContentStatusIcon,
  SessionStatusIcon,
} from '../../../../../app/training/sessionIcons';
import {
  CourseSessionPillConfig,
  TrainingIcon,
} from '../../../../../app/training/coursePills';
import {TrainingDocumentTitle} from '../../../../../app/training/courseContentValidation';
import {getLanguageDisplayName} from 'src/app/training/languageUtils';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {ViewLinkedWorkflowSection} from 'src/app/workflows/ViewLinkedWorkflowSection';
import {CourseSessionQuizResults} from './CourseSessionQuizResults';
import type {ViewCourseSessionRecordViewType} from 'src/shared/constants/ViewTypes';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';


export interface ViewCourseSessionRecordProps {
  isAdminPath: boolean;
}
export const ViewCourseSessionRecord: React.FC<
  ViewCourseSessionRecordProps
> = ({isAdminPath}) => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, assignmentId, sessionId, view} = useParams();

  const trainingPerms = useModulePermissions(ModuleEnum.Training);
  const auditPerms = useAuditLogPermissions();

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const parsedSessionId =
    sessionId !== undefined ? parseInt(sessionId) : undefined;
  const {
    data: blend,
    isLoading,
    error: sessionError,
  } = useGetCourseSessionQuery(parsedSessionId!, {
    skip: parsedSessionId === undefined,
    refetchOnMountOrArgChange: true,
  });
  const [updateCourseSession, {isLoading: isSavingDates}] =
    useUpdateCourseSessionMutation();

  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editStartedAt, setEditStartedAt] = useState<Date | null>(null);
  const [editCompletedAt, setEditCompletedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (sessionError) {
      setAlert(parseErrorMessage(sessionError), 'error');
    }
  }, [sessionError]);

  const handleEdit = () => {
    setEditStartedAt(
      blend?.session.startedAt ? new Date(blend.session.startedAt) : null
    );
    setEditCompletedAt(
      blend?.session.completedAt ? new Date(blend.session.completedAt) : null
    );
    setIsEditingDates(true);
  };

  const handleCancelEditDates = () => setIsEditingDates(false);

  const handleSaveDates = async () => {
    if (blend === undefined) {
      return;
    }
    try {
      await updateCourseSession({
        id: blend.session.id,
        startedAt: editStartedAt ?? undefined,
        completedAt: editCompletedAt ?? undefined,
      }).unwrap();
      setAlert('Session dates were successfully updated.', 'success');
      setIsEditingDates(false);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const isComplete =
    blend?.session?.sessionState === CourseSessionStateEnum.Completed;
  const failedContents =
    blend?.session?.contents?.filter(
      i =>
        i.state === CourseSessionContentStateEnum.Expired ||
        i.state === CourseSessionContentStateEnum.Failed
    ) ?? [];
  const failedContent =
    failedContents.length > 0
      ? failedContents[failedContents.length - 1]
      : undefined;
  const failedContentDef = failedContent
    ? blend?.definition.contents?.filter(
        i => i.id == failedContent?.contentDefinitionId
      )[0]
    : undefined;

  let failureAlertTitle = '';
  let failureAlertBody = '';
  if (
    blend?.session.sessionState &&
    blend?.session.sessionState !== CourseSessionStateEnum.Cancelled
  ) {
    if (failedContent?.state === CourseSessionContentStateEnum.Expired) {
      failureAlertTitle = 'Session Expired';

      switch (failedContentDef?.type) {
        case CourseDefinitionContentTypeEnum.Demonstration:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to
            complete '${
              failedContentDef.demonstration?.headerText ?? 'a demonstration'
            }'
            within the expiration window, so this session has expired.`;
          break;
        case CourseDefinitionContentTypeEnum.Lesson:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to complete '${
            failedContentDef.lesson?.headerText ?? 'a lesson'
          }' within the expiration window, so this session has expired.`;
          break;
        case CourseDefinitionContentTypeEnum.Quiz:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to complete '${
            failedContentDef.quiz?.headerText ?? 'a quiz'
          }' within the expiration window, so this session has expired.`;
          break;
      }
    }

    if (failedContent?.state === CourseSessionContentStateEnum.Failed) {
      failureAlertTitle = 'Session Failed';

      switch (failedContentDef?.type) {
        case CourseDefinitionContentTypeEnum.Demonstration:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to
            complete '${
              failedContentDef.demonstration?.headerText ?? 'a demonstration'
            }'
            , so this session has failed.`;
          break;
        case CourseDefinitionContentTypeEnum.Lesson:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to
            complete '${failedContentDef.lesson?.headerText ?? 'a lesson'}'
            , so this session has failed.`;
          break;
        case CourseDefinitionContentTypeEnum.Quiz:
          failureAlertBody = `${
            blend.assignment.assignedTo?.name ?? 'The trainee'
          } failed to meet the required pass percentage for '${
            failedContentDef.quiz?.headerText ?? 'a quiz'
          }', so this session has failed.`;
          break;
      }
    }
  }

  const {RenderedTabsComponent} = useUrlQueryTabV2<ViewCourseSessionRecordViewType>({
    view: (view ?? '') as ViewCourseSessionRecordViewType,
    defaultView: 'overview',
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: blend && (
          <SplitPane
            pane={
              isAdminPath ? (
                <ViewLinkedWorkflowSection courseSessionId={blend.session.id} />
              ) : undefined
            }>
            {blend.session.sessionType ===
              CourseSessionTypeEnum.AdministratorSignOff && (
              <Alert color="green" mb="md" title="Signed-off by Administrator">
                Administrator {blend.session.creator?.name ?? 'Unknown User'}{' '}
                marked this course as completed on behalf of{' '}
                {blend.assignment.assignedTo?.name ?? 'the trainee'}.
              </Alert>
            )}

            {failureAlertTitle !== '' && (
              <Alert
                color="red"
                mb="md"
                title={failureAlertTitle}
                icon={<SessionStatusIcon state={blend.session.sessionState} />}>
                {failureAlertBody}
              </Alert>
            )}

            {isEditingDates ? (
              <Group grow align="flex-start" mb="sm">
                <DateInput
                  label="Start Date"
                  value={editStartedAt}
                  onChange={setEditStartedAt}
                  clearable
                />
                <DateInput
                  label="Completed Date"
                  value={editCompletedAt}
                  onChange={setEditCompletedAt}
                  clearable
                />
                <Group align="flex-end" gap="xs">
                  <Button
                    size="sm"
                    onClick={handleSaveDates}
                    loading={isSavingDates}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="subtle"
                    color="black"
                    onClick={handleCancelEditDates}
                    disabled={isSavingDates}>
                    Cancel
                  </Button>
                </Group>
              </Group>
            ) : (
              <>
                <FormViewRow
                  label="Start Date"
                  value={blend.session.startedAt?.toString().toShortDate()}
                />
                <FormViewRow
                  label="Completed Date"
                  value={blend.session.completedAt?.toString().toShortDate()}
                />
              </>
            )}
            <FormViewRow
              label="Language"
              value={getLanguageDisplayName(blend.session.language)}
            />

            {blend.definition.isCertificateGenerated === true &&
              isComplete === true && (
                <Box mt="md">
                  <Button
                    size="sm"
                    onClick={() => handleOpenCertificate(sessionId)}>
                    View Course Certificate
                  </Button>
                </Box>
              )}

            <Divider mt="md" mb="md" />

            <Subtitle text="Training Contents" />

            {blend.session.contents?.map((sContent, index) => {
              const dContent = blend.definition.contents?.filter(
                i => i.id == sContent.contentDefinitionId
              )[0];

              if (dContent === undefined) {
                return (
                  <p>Invalid definition for session content {sContent.id}</p>
                );
              }

              const title =
                dContent.quiz?.headerText ??
                dContent.lesson?.headerText ??
                dContent.demonstration?.headerText;

              return (
                <Stack key={sContent.id} mt="md" gap="sm">
                  <Group gap="sm">
                    <SessionContentStatusIcon state={sContent.state} />

                    <Title size="sm">
                      {index + 1}. {title}
                    </Title>
                  </Group>

                  {dContent?.quiz?.enableGenerateCertificate === true &&
                    isComplete === true && (
                      <Box>
                        <Button
                          size="sm"
                          onClick={() =>
                            handleOpenCertificate(
                              sessionId,
                              String(sContent.id)
                            )
                          }>
                          View Certificate
                        </Button>
                      </Box>
                    )}
                  {sContent?.demonstration?.demonstrationFormId &&
                    isComplete === true && (
                      <Box>
                        <Button
                          size="sm"
                          onClick={() =>
                            handleOpenDemonstration(dContent, sContent)
                          }>
                          View Demonstration Form
                        </Button>
                      </Box>
                    )}
                  {dContent?.demonstration?.skillId &&
                    (() => {
                      const signOff = sContent?.demonstration?.skillSignOff;
                      if (!signOff) {
                        return null;
                      }
                      return (
                        <Box>
                          <Group gap="xs" mb="xs">
                            <Badge
                              color={
                                signOff.result ===
                                SkillSignOffResultEnum.SignedOff
                                  ? 'green'
                                  : signOff.result ===
                                    SkillSignOffResultEnum.NotYetCompetent
                                  ? 'red'
                                  : 'gray'
                              }>
                              {signOff.result ===
                              SkillSignOffResultEnum.SignedOff
                                ? 'Signed off'
                                : signOff.result ===
                                  SkillSignOffResultEnum.NotYetCompetent
                                ? 'Not yet competent'
                                : 'Pending'}
                            </Badge>
                            {signOff.signedOffByName && (
                              <Text size="xs" c="dimmed">
                                by {signOff.signedOffByName}
                                {signOff.signedOffAt
                                  ? ` on ${parseUtcDate(
                                      signOff.signedOffAt
                                    ).toLocaleDateString()}`
                                  : ''}
                              </Text>
                            )}
                          </Group>
                          {signOff.criterionResults.length > 0 && (
                            <Stack gap={4}>
                              {signOff.criterionResults.map(result => (
                                <Box key={result.criterionId}>
                                  <Group gap="xs">
                                    <Badge
                                      size="xs"
                                      variant="outline"
                                      color={
                                        result.result ===
                                        SkillSignOffCriterionResultEnum.Pass
                                          ? 'green'
                                          : result.result ===
                                            SkillSignOffCriterionResultEnum.Fail
                                          ? 'red'
                                          : 'gray'
                                      }>
                                      {result.result ===
                                      SkillSignOffCriterionResultEnum.Pass
                                        ? 'Pass'
                                        : result.result ===
                                          SkillSignOffCriterionResultEnum.Fail
                                        ? 'Fail'
                                        : 'N/A'}
                                    </Badge>
                                    <Text size="xs">
                                      {result.criterionText}
                                    </Text>
                                  </Group>
                                  {result.note && (
                                    <Text size="xs" c="dimmed">
                                      {result.note}
                                    </Text>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          )}
                          {signOff.comment && (
                            <Text size="xs" c="dimmed" mt="xs">
                              Comment: {signOff.comment}
                            </Text>
                          )}
                        </Box>
                      );
                    })()}
                  {isAdminPath && dContent?.quiz && sContent?.quiz && (
                    <Accordion>
                      <Accordion.Item value="quiz-results">
                        <Accordion.Control>
                          <Text size="sm" c="dimmed">
                            Quiz Results
                          </Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <CourseSessionQuizResults
                            dContent={dContent}
                            sContent={sContent}
                          />
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </Stack>
              );
            })}
          </SplitPane>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: sessionId && (
          <AuditModule
            entityId={String(sessionId)}
            entityType={EntityIdentifierEnum.CourseSession}
          />
        ),
      },
    ],
    onSelectView: view => {
      const url = isAdminPath
        ? PathUtility.TrainingCourseSessionView(
            String(definitionId),
            String(assignmentId),
            String(sessionId),
            view as any
          )
        : PathUtility.TrainingSessionRecord(String(sessionId), view as any);
      navigate(url, {replace: true});
    },
  });

  function handleOpenCertificate(sessionId?: string, contentId?: string): void {
    const url = isAdminPath
      ? PathUtility.TrainingCourseSessionCertificate(
          String(definitionId),
          String(assignmentId),
          String(sessionId),
          contentId
        )
      : PathUtility.TrainingSessionCertificate(sessionId, contentId);
    navigate(url);
  }

  function handleOpenDemonstration(
    dContent: CourseDefinitionContentResponse,
    sContent: CourseSessionContentResponse
  ): void {
    const formDefinitionId = Number(
      dContent?.demonstration?.demonstrationFormDefinitionId
    );
    const formSubmissionId = Number(
      sContent?.demonstration?.demonstrationFormId
    );
    navigate(
      PathUtility.FormSubmission(formDefinitionId, formSubmissionId, ':view')
    );
  }

  const canEdit = isAdminPath && trainingPerms.update && isComplete;

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: isAdminPath ? 'session' : 'user-session',
    definitionId: definitionId,
    courseName: blend?.definition.name,
    assignmentId: isAdminPath ? assignmentId : String(blend?.assignment.id),
    assigneeName: blend?.assignment.assignedTo?.name,
    sessionId: sessionId,
    sessionIdentifier: blend?.session.internalIdentifier,
  });

  const pills = CourseSessionPillConfig(
    blend?.session.sessionState ?? CourseSessionStateEnum.Pending,
    blend?.session.sessionType ?? CourseSessionTypeEnum.Standard
  );

  return !isLoading && blend ? (
    <Box>
      <BackBar
        fallbackTo={fallbackTo}
        fallbackLabel={fallbackLabel}
        breadcrumbs={breadcrumbs}
      />
      <PageHeader>
        <PageHeader.Title icon={TrainingIcon}>
          {blend?.assignment.assignedTo?.name ?? 'Session'}
        </PageHeader.Title>
        {blend?.session.internalIdentifier && (
          <PageHeader.Meta>{blend.session.internalIdentifier}</PageHeader.Meta>
        )}
        <PageHeader.Meta>Course: {blend?.definition.name}</PageHeader.Meta>
        {pills.length > 0 && (
          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>
        )}
        {canEdit && (
          <PageHeader.Actions>
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          </PageHeader.Actions>
        )}
      </PageHeader>

      {RenderedTabsComponent}
    </Box>
  ) : (
    <Spinner loadingText="Loading...." />
  );
};
